// Well control in the rotary era (M2_SPEC §4, DESIGN §5.3): kicks, the BOP, killing the well, blowouts and
// M2 gushers (3 × qi, captured into pits/tanks, gas-cap fires, H₂S on the floor, waste fines from 1930).
//
//   kick → BOP (ram_bop & later, not the capping valve) holds with 1 − failChance × (1 + (100 − cond)/100) × bopFailMult
//          → SHUT_IN_KICK (1 d) → KILLING (rand(killDays) × kickKillDaysMult) → MW = pore + MUD.killMarginPpg → back to work
//        → otherwise: a reservoir before 1931 → GUSHER (decision: let her blow / cap it / shut in with a BOP),
//          anything else → BLOWOUT (wild well, CAPPING 5–30 d, 10%/d fire).
import { allWells } from './indexes.js'; // perf: per-day well list (same order as Object.values)
import { chance, range, int, nextFloat } from '../core/rng.js';
import { MUD, WASTE } from '../data/m2/rules.js';
import { eqDef, fieldDef, stat, eraDollars, yf, isAvailable } from './defs.js';
import { reservoirsAt } from './geo.js';
import { cancelTimer } from './timers.js';
import { activeRigCrew, injureCrew, roleName } from './crew.js';
import { mitigation } from './safety.js';
import { queueDecision, popDecision } from './security.js';
import { effects } from './research.js';
import { storeOil } from './production.js';
import { wellRig, setTask, clearTask, completeImpl } from './drilling.js';
import { mudWeight, bestMudFor, baritePerFt, learnPore, mudDef } from './mud.js';
import { clamp, notify, log, spend, fmt$ } from './util.js';
import { bump } from './m2util.js';
import { m4BopFailMult } from './m4/worldHooks.js'; // M4-C

export const DECISION_DAYS = 3;
export const CAPPING_CREW_COST = 1000;       // 1901 $
export const BLOWOUT_CAP_DAYS = [5, 30];
export const GUSHER_CAP_DAYS = [3, 15];
export const BLOWOUT_FIRE_PER_DAY = 0.1;
export const GASCAP_FIRE_PER_DAY = 0.02;
export const GUSHER_FIRE_PER_DAY = 0.005;

// ---- equipment on the rig ----------------------------------------------------------------
export function rigBop(state, rig) {
  const it = rig?.bopUid ? state.items[rig.bopUid] : null;
  if (!it) return null;
  const def = eqDef(it.defId);
  return def && !stat(def, 'cappingOnly', false) ? { item: it, def } : null;
}

export function cappingMult(state, rig) {
  let m = 1;
  for (const uid of [rig?.gear?.capping, rig?.bopUid]) {
    const it = uid ? state.items[uid] : null;
    const def = it ? eqDef(it.defId) : null;
    if (def && stat(def, 'cappingOnly', false)) m = Math.min(m, stat(def, 'cappingDaysMult', 0.4));
  }
  return m;
}

// M4 balance: from 2000 a chartered rig (well.contract.charter — no player rig on the well) has the contractor's BOP
// (best subsea stack of the day offshore, a 15k surface stack on land, kept at 90%) instead of none, which turned every
// kick on a chartered drillship into an uncontrolled deepwater blowout. Pre-2000 behaviour is unchanged.
// A drilling contractor brings the stack of its day: a chartered rig (2000+) a modern one, a hired contractor
// (M2 §1.5) the best ordinary BOP on the market — none before the ram BOP (1922).
function contractorBop(state, well) {
  if (!well?.contract) return null;
  const sea = (well.waterDepthFt ?? 0) > 0;
  const ids = well.contract.charter && yf(state) >= 2000 ? (sea ? ['bop_dual_shear', 'subsea_bop_mux', 'subsea_bop'] : ['bop_stack_15k', 'annular_bop'])
    : (sea ? ['subsea_bop', 'annular_bop', 'ram_bop'] : ['annular_bop', 'ram_bop']);
  const def = ids.map(eqDef).find((d) => d && isAvailable(d, state));
  return def ? { item: { condition: 90 }, def } : null;
}
export function bopFailChance(state, bop) {
  if (!bop) return 1;
  const cond = bop.item.condition ?? 100;
  return clamp(stat(bop.def, 'failChance', 0.05) * (1 + (100 - cond) / 100) * (effects(state).bopFailMult ?? 1), 0, 1);
}

// ---- kicks -------------------------------------------------------------------------------
// zone: from mud.js zoneProfile ({ kind, key, pore, resId?, h2s, name, top, ... }).
export function onKick(state, well, zone) {
  const rig = wellRig(state, well);
  bump(state, 'kicks');
  learnPore(state, well, zone);
  const ppg = Math.round(zone.pore * 10) / 10;
  // a kick from a missed pay sand reveals it
  if (zone.resId && (well.bypassed ?? []).includes(zone.resId)) {
    well.bypassed = well.bypassed.filter((x) => x !== zone.resId);
    if (!well.revealed.includes(zone.resId)) well.revealed.push(zone.resId);
    if (!well.reservoirId) { well.reservoirId = zone.resId; well.payTop = zone.top; well.strikeDay ??= state.day; well.strikeRigCond ??= rig?.condition ?? 100; }
  }
  if (well.timerId) { cancelTimer(state, well.timerId); clearTask(state, well); }
  const bop = rigBop(state, rig) ?? contractorBop(state, well); // M4 balance: a chartered rig brings its own stack
  const fail = bopFailChance(state, bop) * (well.waterDepthFt > 0 ? m4BopFailMult(state, well) : 1); // M4-C: Well Control Rule (2016) BOP tests
  const controlled = !!bop && (state._rush || !chance(state, fail));
  well.kick = { day: state.day, ppg, zone: zone.name, zoneKey: zone.key, kind: zone.kind, controlled, gain: Math.round(range(state, 5, 40)) };
  const where = `${Math.round(zone.top)} ft (${zone.name}, ~${ppg.toFixed(1)} ppg)`;
  if (controlled) {
    well.status = 'SHUT_IN_KICK';
    setTask(state, well, 'SHUT_IN', 1, { next: 'KILL', killPpg: Math.round((ppg + MUD.killMarginPpg) * 10) / 10, resume: well.reservoirId && zone.resId === well.reservoirId && well.depth >= well.target - 1e-6 ? 'AT_TD' : 'DRILLING' });
    if (bop) bop.item.condition = Math.max(0, bop.item.condition - 5);
    notify(state, 'kick', { wellId: well.id, fieldId: well.fieldId, controlled: true, ppg, zone: zone.name, pause: true, level: 'warn',
      text: `${well.name}: KICK at ${where}! The ${bop.def.name} slammed shut — well shut in. Killing it with ${(ppg + MUD.killMarginPpg).toFixed(1)} ppg mud.` });
    h2sExposure(state, well, rig, zone.h2s ?? 0, 0.3);
    return 'controlled';
  }
  notify(state, 'kick', { wellId: well.id, fieldId: well.fieldId, controlled: false, ppg, zone: zone.name, level: 'warn',
    text: `${well.name}: kick at ${where}${bop ? ` — the ${bop.def.name} failed` : ' — no blowout preventer'}!` });
  if (zone.kind === 'reservoir' && yf(state) < 1931) {
    const r = reservoirsAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]).find((x) => x.id === zone.resId);
    if (r) {
      if (!well.reservoirId) { well.reservoirId = r.id; well.payTop = r.top; }
      if (!well.revealed.includes(r.id)) well.revealed.push(r.id);
      startM2Gusher(state, well, rig, r);
      return 'gusher';
    }
  }
  startBlowout(state, well, rig, zone);
  return 'blowout';
}

// SHUT_IN (1 d) → KILL timer
export function onShutInDone(state, well, t) {
  const rig = wellRig(state, well);
  const [lo, hi] = stat(rigBop(state, rig)?.def, 'killDays', [2, 4]);
  const days = Math.max(1, Math.round(range(state, lo, hi + 0.999) * (effects(state).kickKillDaysMult ?? 1) - 0.499));
  well.status = 'KILLING';
  setTask(state, well, 'KILL', days, { killPpg: t.data.killPpg, resume: t.data.resume, fromGusher: t.data.fromGusher });
}

// Weight up to kill weight (switching mud if the current one can't hold it), charge barite for the whole hole.
export function weightUp(state, well, ppg, { charge = true } = {}) {
  const cur = mudDef(well);
  const def = bestMudFor(state, ppg, cur?.id) ?? cur;
  if (def && def !== cur) well.mud.defId = def.id;
  const max = def ? stat(def, 'ppgMax', 10) : ppg;
  const target = Math.min(ppg, max);
  const old = mudWeight(well);
  well.mud.ppg = Math.round(Math.max(old, target) * 10) / 10;
  if (charge && def) {
    const cost = well.depth * (eraDollars(state, stat(def, 'pricePerFt', 0.1), def.baseYear ?? def.year) + baritePerFt(state, well.mud.ppg));
    if (cost > 0) { spend(state, cost, 'consumables', false); well.cost += cost; }
  }
  return well.mud.ppg >= ppg - 1e-9;
}

export function onKillDone(state, well, t) {
  const ok = weightUp(state, well, t.data.killPpg);
  bump(state, 'bopSaves');
  well.kick = well.kick ? { ...well.kick, killedDay: state.day } : null;
  const resume = t.data.resume ?? 'DRILLING';
  notify(state, 'kick', { wellId: well.id, fieldId: well.fieldId, controlled: true, killed: true, ppg: Math.round(well.mud.ppg * 10) / 10,
    text: `${well.name}: well killed — mud weighted to ${Number(well.mud.ppg).toFixed(1)} ppg${ok ? '' : ` (the most this mud can hold; ${t.data.killPpg.toFixed(1)} needed — watch it)`}. Back to work.` });
  if (t.data.fromGusher || resume === 'AT_TD') {
    well.status = 'AT_TD';
    well.target = Math.min(well.target, well.depth);
    const rig = wellRig(state, well);
    if (rig?.auto?.complete !== false) completeImpl(state, well);
    return;
  }
  well.status = resume;
}

// ---- M2 gushers ----------------------------------------------------------------------------
export function gusherQi(state, well, r) {
  const plot = state.fields[well.fieldId].plots[well.plot];
  const [lo, hi] = r.qiRange ?? [10, 60];
  const roll = plot.truth.qiRoll ?? 0.5;
  const edgeF = 1 - 0.35 * clamp(plot.truth.edge ?? 0, 0, 1);
  const res = state.fields[well.fieldId].reservoirs[r.id];
  return Math.max(1, (lo + (hi - lo) * roll) * edgeF * clamp(res?.pressure ?? 1, 0.2, 1));
}

export function startM2Gusher(state, well, rig, r) {
  const qi = gusherQi(state, well, r);
  well.status = 'GUSHER';
  well.gushed = true;
  well.payFt = r.thick;
  well.target = Math.min(well.target, well.depth);
  well.strikeDay ??= state.day;
  well.strikeRigCond ??= rig?.condition ?? 100;
  well.gusher = { rate: Math.round(qi * 3), qiAfter: Math.round(qi), startDay: state.day, captured: 0, lost: 0, m2: true, gasCap: !!r.gasCap, h2s: r.h2s ?? 0, finedLost: 0, decision: null };
  const days = Math.max(1, Math.round(int(state, GUSHER_CAP_DAYS[0], GUSHER_CAP_DAYS[1]) * cappingMult(state, rig)));
  setTask(state, well, 'CAPPING', days);
  state.company.stats.gushers++;
  state.company.stats.strikes++;
  state.company.stats.dryStreak = 0;
  const waste = yf(state) >= WASTE.from;
  if (waste) state.company.reputation = Math.max(0, state.company.reputation - 2);
  const text = `GUSHER! ${well.name} blew in at ${Math.round(well.depth)} ft — ~${Math.round(qi * 3).toLocaleString('en-US')} bbl/d over the crown block!${waste ? ' The conservation inspectors will fine every barrel wasted.' : ''}`;
  notify(state, 'gusher', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'major', decision: !waste, text });
  h2sExposure(state, well, rig, r.h2s ?? 0, 1);
  if (!waste && !well.ownerOperatorId) {
    const bop = rigBop(state, rig);
    const capCost = eraDollars(state, CAPPING_CREW_COST, 1901);
    queueDecision(state, {
      kind: 'gusher', id: `gush_${well.id}`, wellId: well.id, day: state.day, title: `Gusher at ${well.name}`,
      text: `${well.name} is blowing ~${Math.round(qi * 3).toLocaleString('en-US')} bbl/d over the derrick. Let her blow (fame, fills the pits), bring in a capping crew, or close the rams?`,
      options: [
        { choice: 'blow', label: 'Let her blow' },
        { choice: 'cap', label: `Cap it (${fmt$(capCost)})`, cost: capCost },
        ...(bop ? [{ choice: 'shutin', label: `Shut in with the ${bop.def.name}` }] : []),
      ],
    });
  }
}

export function resolveGusherImpl(state, decisionId, choice, auto = false) {
  const all = [state.pendingDecision, ...(state.decisionQueue ?? [])].filter(Boolean);
  const d = all.find((x) => x.id === decisionId);
  if (!d || d.kind !== 'gusher') return { ok: false, msg: 'No gusher decision pending.' };
  const well = state.wells[d.wellId];
  if (!well || well.status !== 'GUSHER') { popDecision(state, d.id); return { ok: true, msg: 'The gusher is already under control.' }; }
  const rig = wellRig(state, well);
  const t = well.timerId ? state.timers[well.timerId] : null;
  if (choice === 'cap') {
    const cost = eraDollars(state, CAPPING_CREW_COST, 1901);
    if (!spend(state, cost, 'other')) return { ok: false, msg: `A capping crew costs ${fmt$(cost)}.` };
    well.cost += cost;
    if (t) { t.daysLeft = Math.max(1, Math.ceil(t.daysLeft / 2)); if (rig?.task) rig.task.daysLeft = t.daysLeft; }
    well.gusher.decision = 'cap';
    popDecision(state, d.id);
    return { ok: true, msg: `Capping crew on the way: ~${t?.daysLeft ?? 1} days.` };
  }
  if (choice === 'shutin') {
    const bop = rigBop(state, rig);
    if (!bop) return { ok: false, msg: 'There is no blowout preventer on this well.' };
    if (t) cancelTimer(state, t.id);
    clearTask(state, well);
    well.gusher.decision = 'shutin';
    well.status = 'SHUT_IN_KICK';
    const r = reservoirsAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]).find((x) => x.id === well.reservoirId);
    setTask(state, well, 'SHUT_IN', 1, { next: 'KILL', killPpg: Math.round(((r?.pore ?? 10) + MUD.killMarginPpg) * 10) / 10, resume: 'AT_TD', fromGusher: true });
    popDecision(state, d.id);
    return { ok: true, msg: `Rams closed on ${well.name}. Killing the well, then completing it.` };
  }
  // let her blow
  well.gusher.decision = 'blow';
  if (!auto) state.company.reputation = Math.min(100, state.company.reputation + 3);
  popDecision(state, d.id);
  return { ok: true, msg: `Let her blow! The whole county turns out to watch ${well.name}.` };
}

// ---- blowouts ------------------------------------------------------------------------------
export function startBlowout(state, well, rig, zone) {
  const r = zone.resId ? reservoirsAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]).find((x) => x.id === zone.resId) : null;
  const rate = r ? Math.round(gusherQi(state, well, r) * 3) : 0;
  well.status = 'BLOWOUT';
  well.blowout = { day: state.day, kind: zone.kind, zone: zone.name, zoneKey: zone.key, pore: zone.pore, resId: r?.id ?? null, rate, lost: 0, captured: 0, finedLost: 0, h2s: zone.h2s ?? 0 };
  const days = Math.max(1, Math.round(int(state, BLOWOUT_CAP_DAYS[0], BLOWOUT_CAP_DAYS[1]) * cappingMult(state, rig)));
  setTask(state, well, 'CAPPING', days, { blowout: true });
  bump(state, 'blowouts');
  state.company.reputation = Math.max(0, state.company.reputation - 5);
  if (rig) rig.condition = Math.max(0, rig.condition - range(state, 5, 20));
  notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'warn',
    text: `BLOWOUT at ${well.name}! ${zone.kind === 'reservoir' ? 'Oil and gas' : 'Gas'} from the ${zone.name} roaring out of the hole. Capping it will take ~${days} days.` });
  h2sExposure(state, well, rig, zone.h2s ?? 0, 1);
}

export function onBlowoutCapped(state, well, rushed) {
  const b = well.blowout ?? {};
  const rig = wellRig(state, well);
  well.fire = well.fire && well.fire.untilDay > state.day ? { ...well.fire, untilDay: state.day } : well.fire;
  notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, capped: true,
    text: `${well.name}: the wild well is capped${rushed ? ' early' : ''}.${b.lost ? ` ${Math.round(b.lost).toLocaleString('en-US')} bbl lost.` : ''}` });
  weightUp(state, well, (b.pore ?? mudWeight(well)) + MUD.killMarginPpg);
  well.blowout = null;
  if (b.resId && well.reservoirId === b.resId) {
    well.status = 'AT_TD';
    well.target = Math.min(well.target, well.depth);
    if (rig?.auto?.complete !== false) completeImpl(state, well);
  } else well.status = rig || well.contract ? 'DRILLING' : 'SUSPENDED';
}

// ---- daily: gushers & blowouts (after production has flowed the gushers) ------------------------
export function tickWellControl(state) {
  const y = yf(state);
  const waste = y >= WASTE.from;
  for (const well of allWells(state)) {
    if (well.status === 'GUSHER' && well.gusher?.m2) gusherDay(state, well, waste);
    else if (well.status === 'BLOWOUT' && well.blowout) blowoutDay(state, well, waste);
  }
  // unanswered gusher decisions default to "let her blow"
  for (const d of [state.pendingDecision, ...(state.decisionQueue ?? [])]) {
    if (d?.kind === 'gusher' && state.day - d.day >= DECISION_DAYS) resolveGusherImpl(state, d.id, 'blow', true);
  }
}

function fireRoll(state, well, rig, p) {
  if (state._rush || !chance(state, p)) return false;
  well.fire = { day: state.day, untilDay: state.day + int(state, 3, 8) };
  if (rig) rig.condition = Math.max(0, rig.condition - range(state, 20, 60));
  const crew = rig ? activeRigCrew(state, rig) : [];
  if (crew.length && chance(state, 0.4)) injureCrew(state, crew[Math.floor(nextFloat(state) * crew.length)], 'was burned when the well caught fire', { cause: 'well fire', fatalChance: 0.25, wellId: well.id, accident: 'well_fire' });
  notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'warn', text: `${well.name} caught FIRE!` });
  return true;
}

function fineWaste(state, well, rec, waste) {
  if (!waste) return;
  const lostNow = rec.lost - (rec.finedLost ?? 0);
  if (lostNow <= 0) return;
  const fine = lostNow * eraDollars(state, WASTE.finePerBbl, WASTE.baseYear);
  rec.finedLost = rec.lost;
  spend(state, fine, 'other', false);
  bump(state, 'wasteBbl', lostNow);
  bump(state, 'wasteFines', fine);
  well.cost += fine;
}

function gusherDay(state, well, waste) {
  const rig = wellRig(state, well);
  const g = well.gusher;
  const near = (rig?.safety?.boilerSpacingFt ?? 30) < 100 ? 2 : 1;
  if (!(well.fire && well.fire.untilDay > state.day)) fireRoll(state, well, rig, (g.gasCap ? GASCAP_FIRE_PER_DAY : GUSHER_FIRE_PER_DAY) * near);
  if (g.h2s > 0 && chance(state, 0.15)) h2sExposure(state, well, rig, g.h2s, 0.5);
  fineWaste(state, well, g, waste);
}

function blowoutDay(state, well, waste) {
  const rig = wellRig(state, well);
  const b = well.blowout;
  const field = state.fields[well.fieldId];
  if (b.resId && b.rate > 0) {
    const res = field.reservoirs[b.resId];
    const flow = res ? Math.min(res.remaining, b.rate * Math.max(0.2, res.pressure)) : 0;
    if (res) res.remaining -= flow;
    // DESIGN §5.3: eras 1–2 save ~20% in earthen pits
    const pits = Object.values(state.items).some((i) => i.loc.type === 'field' && i.loc.id === well.fieldId && i.defId === 'earthen_pit');
    const stored = pits && !well.ownerOperatorId ? storeOil(state, well.fieldId, flow * 0.2) : 0;
    b.captured += stored;
    b.lost += flow - stored;
    state.company.stats.bblProduced += stored;
    state.company.stats.bblSpilled += flow - stored;
  }
  const burning = well.fire && well.fire.untilDay > state.day;
  if (burning) {
    if (rig) rig.condition = Math.max(0, rig.condition - 30);
    well.fire.untilDay = Math.max(well.fire.untilDay, state.day + 1);
  } else fireRoll(state, well, rig, BLOWOUT_FIRE_PER_DAY);
  if (b.h2s > 0 && chance(state, 0.2)) h2sExposure(state, well, rig, b.h2s, 0.5);
  fineWaste(state, well, b, waste);
}

// ---- H₂S on the floor (DESIGN §15) ---------------------------------------------------------
// One exposure roll per crew member on site: 5% per 100 ppm, ×2 above 500 ppm, scaled (0.3 for a shut-in kick).
export function h2sExposure(state, well, rig, ppm, scale = 1) {
  if (!(ppm > 0) || !rig || state._rush) return 0;
  const crew = activeRigCrew(state, rig);
  if (!crew.length) return 0;
  const keep = (1 - mitigation(state, rig, 'h2s')) * (effects(state).h2sIncidentMult ?? 1);
  const p = clamp(0.05 * (ppm / 100) * (ppm > 500 ? 2 : 1) * scale, 0, 0.8) * keep;
  const hit = crew.filter(() => chance(state, p));
  if (!hit.length) return 0;
  if (!state.safety.seen.h2s) {
    state.safety.seen.h2s = state.day;
    notify(state, 'warning', { nearMiss: true, incidentType: 'h2s', wellId: well.id, text: `Near miss — sour gas: ${hit[0].name} staggered off the floor at ${well.name} when the rotten-egg smell suddenly stopped. Get upwind!`, level: 'warn' });
    return 0;
  }
  const fatal = ppm > 500 ? 0.5 : 0.3;
  const fates = hit.map((c) => `${c.name} (${roleName(c.role)}) ${injureCrew(state, c, 'was knocked down by sour gas', { cause: 'H₂S', fatalChance: fatal, wellId: well.id, accident: 'h2s', silent: true, days: [5, 40] }) === 'dead' ? 'was killed' : 'was overcome'}`);
  notify(state, 'accident', { incidentType: 'h2s', wellId: well.id, fieldId: well.fieldId, rigUid: rig.uid, pause: true, level: 'warn',
    text: `Sour gas (${Math.round(ppm)} ppm H₂S) at ${well.name}: ${fates.join('; ')}.` });
  return hit.length;
}

export const logKick = (state, well, text) => log(state, text, 'warn', { wellId: well.id });
